<template>
  <div class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-center py-8 justify-center min-h-screen">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>
      <div
        class="bg-white rounded-lg overflow-hidden shadow-xl z-50 px-6 py-6 min-w-[90vw] md:min-w-[40vw]"
      >
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-medium text-gray-800 dark:text-white">
            Actualizar datos del comprobante
          </h2>
          <button
            @click="isActive"
            class="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-lg"
          >
            <svg class="w-5 h-5">
              <use href="../../../assets/svg/icon.svg#cancel" />
            </svg>
          </button>
        </div>
        <LoadingForms v-if="!isLoading" />
        <form class="w-full space-y-6" @submit.prevent v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="md:col-span-2 lg:col-span-3">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                Modalidad del comprobante
              </span>
              <div class="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
                <button
                  type="button"
                  class="rounded-lg px-4 py-2 text-sm font-semibold transition"
                  :class="dataForm.voucherMode === 'physical' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'"
                  @click="setVoucherMode('physical')"
                >
                  Comprobante físico
                </button>
                <button
                  type="button"
                  class="rounded-lg px-4 py-2 text-sm font-semibold transition"
                  :class="dataForm.voucherMode === 'electronic' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'"
                  @click="setVoucherMode('electronic')"
                >
                  Comprobante electrónico
                </button>
              </div>
              <p class="mt-2 text-xs text-gray-500">
                {{ sequenceModeHelpText }}
              </p>
              <p class="mt-1 text-xs font-medium text-gray-600">
                Modalidad activa guardada: {{ activeModeLabel }}. Solo esta modalidad se usa al vender; la otra conserva su correlación guardada.
              </p>
            </div>
            <label class="block" v-if="!isElectronicMode">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                Serie comprobante Físico
              </span>
              <input
                type="text"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                v-model="dataForm.serie"
                placeholder="B001"
                @input="sanitizePhysicalSerieField"
                @keydown.enter.prevent
                maxlength="4"
              />
            </label>
            <label class="block" v-else>
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                Serie comprobante Electrónico
              </span>
              <input
                type="text"
                maxlength="3"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.invoice_number"
                placeholder="E31"
                @input="sanitizeElectronicSerieField"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                N° de inicio
              </span>
              <input
                type="text"
                :maxlength="sequenceLength"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.numberStart"
                :placeholder="sequencePlaceholder"
                inputmode="numeric"
                @input="sanitizeNumericField('numberStart', sequenceLength)"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                N° actual
              </span>
              <input
                type="text"
                :maxlength="sequenceLength"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 border-green-500 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.numberCurrent"
                :placeholder="sequencePlaceholder"
                inputmode="numeric"
                @input="sanitizeNumericField('numberCurrent', sequenceLength)"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                N° final
              </span>
              <input
                type="text"
                :maxlength="sequenceLength"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 border-orange-300 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.numberFinal"
                :placeholder="sequenceFinalPlaceholder"
                inputmode="numeric"
                @input="sanitizeNumericField('numberFinal', sequenceLength)"
                @keydown.enter.prevent
              />
            </label>
            <label class="block" v-if="showExpirationDate">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                Fecha de vencimiento
              </span>
              <input
                type="date"
                maxlength="8"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 border-gray-300 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.vencimiento"
                placeholder="Fecha de vencimiento"
                @keydown.enter.prevent
              />
            </label>
            <label class="block">
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                N° de envio {{ config.auditingInstitution }}
              </span>
              <input
                type="text"
                maxlength="8"
                class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 border-gray-300 focus:border-blue-500 -moz-appearance-none -webkit-appearance-none appearance-none"
                v-model="dataForm.numberOfSendingSunat"
                placeholder="N° envio"
                @keydown.enter.prevent
              />
            </label>
            <label
              class="flex flex-col h-6 mr-5 cursor-pointer mb-[3.5rem!important]"
            >
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                ¿Trabajando?
              </span>
              <div
                class="relative inline-flex items-center h-6 mr-5 mt-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="sr-only peer"
                  v-model="dataForm.working"
                />
                <div
                  class="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"
                ></div>
                <span
                  class="ml-3 text-sm font-medium text-gray-600 dark:text-gray-300"
                >
                  {{ dataForm.working === true ? "Si" : "No" }}
                </span>
              </div>
            </label>
            <label
              class="flex flex-col h-6 mr-5 cursor-pointer mb-[3.5rem!important]"
            >
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                ¿Fijado?
              </span>
              <div
                class="relative inline-flex items-center h-6 mr-5 mt-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="sr-only peer"
                  v-model="dataForm.fixed"
                />
                <div
                  class="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"
                ></div>
                <span
                  class="ml-3 text-sm font-medium text-gray-600 dark:text-gray-300"
                >
                  {{ dataForm.fixed === true ? "Si" : "No" }}
                </span>
              </div>
            </label>
            <label
              class="flex flex-col h-6 mr-5 cursor-pointer mb-[1rem!important]"
            >
              <span
                class="block mb-2 text-sm font-medium text-gray-800 dark:text-white"
              >
                Estado actual
              </span>
              <div
                class="relative inline-flex items-center h-6 mr-5 mt-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="sr-only peer"
                  v-model="dataForm.state"
                />
                <div
                  class="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"
                ></div>
                <span
                  class="ml-3 text-sm font-medium text-gray-600 dark:text-gray-300"
                >
                  {{ dataForm.state === true ? "Activo" : "Inactivo" }}
                </span>
              </div>
            </label>
          </div>

          <p class="text-xs text-gray-500">
            {{ sequenceValidationSummary }}
          </p>

          <div class="flex mt-[3.5rem!important]">
            <button
              class="bg-blue-500 text-white px-4 py-2 font-semibold text-sm rounded-lg hover:shadow-sm hover:shadow-blue-600 active:scale-95 flex items-center"
              @click="update"
              :disabled="spinner === true"
            >
              <div class="flex items-center" v-if="spinner">
                <svg class="w-4 h-4 mr-3 animate-spin">
                  <use href="../../../assets/svg/icon.svg#spinnerForBlue" />
                </svg>
                Modificando Información
              </div>
              <span v-else>Modificar</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { confirmationOfUpdate, errorActions, warningActions } from "@/alerts";
import { observer } from "@/observer";
import axios from "../../../api";
import Swal from "sweetalert2";
import { voucherSupportsExpiration } from "@/utils/voucherExpirationRules";
import config from "/public/apis/config.json";

//componente de loading
import LoadingForms from "@/components/Loadings/LoadingFormsLarge.vue";
export default {
  name: "UpdateVouchers",
  components: {
    LoadingForms,
  },
  props: {
    activeModal: {
      type: Function,
      required: true,
    },
    getdata: {
      type: Function,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const PHYSICAL_SEQUENCE_LENGTH = 8;
    const ELECTRONIC_SEQUENCE_LENGTH = 10;
    const ELECTRONIC_SERIE_MAX_LENGTH = 3;

    async function isActive() {
      await props.activeModal(true);
    }

    const spinner = ref(false);
    const selectedVoucherTypeDescription = ref("");
    const originalConfig = ref({
      numberCurrent: "",
      numberFinal: "",
      vencimiento: "",
    });

    const dataForm = ref({
      voucherMode: "physical",
      serie: "",
      numberStart: "",
      invoice_number: "",
      numberCurrent: "",
      numberFinal: "",
      vencimiento: "",
      working: false,
      fixed: false,
      state: false,
      numberOfSendingSunat: 0,
    });

    const modeDrafts = ref({
      physical: {
        serie: "",
        numberStart: "",
        numberCurrent: "",
        numberFinal: "",
      },
      electronic: {
        invoice_number: "",
        numberStart: "",
        numberCurrent: "",
        numberFinal: "",
      },
    });

    const normalizeVoucherMode = (value, data = {}) => {
      const normalized = String(value ?? "")
        .trim()
        .toLowerCase();

      if (["physical", "fisico", "físico"].includes(normalized)) {
        return "physical";
      }

      if (["electronic", "electronico", "electrónico"].includes(normalized)) {
        return "electronic";
      }

      const hasElectronicDraft = [
        data.electronic_number_start,
        data.electronic_number_current,
        data.electronic_number_final,
      ].some((field) => String(field ?? "").trim() !== "");

      const hasPhysicalDraft = [
        data.physical_number_start,
        data.physical_number_current,
        data.physical_number_final,
      ].some((field) => String(field ?? "").trim() !== "");

      if (hasElectronicDraft && !hasPhysicalDraft) {
        return "electronic";
      }

      return /^E\d{2}$/i.test(String(data.invoice_number ?? "").trim())
        ? "electronic"
        : "physical";
    };

    const normalizeLoadedSequenceValue = (value, length) => {
      const rawValue = String(value ?? "").trim();

      if (!rawValue) {
        return "";
      }

      const digits = rawValue.replace(/\D/g, "");

      if (!digits) {
        return "";
      }

      if (digits.length === length) {
        return digits;
      }

      if (digits.length > length) {
        return digits.slice(-length);
      }

      return digits;
    };

    const resolveLoadedSequenceSet = (data, mode, activeMode) => {
      const prefix = mode === "electronic" ? "electronic" : "physical";
      const sequenceLength =
        mode === "electronic"
          ? ELECTRONIC_SEQUENCE_LENGTH
          : PHYSICAL_SEQUENCE_LENGTH;

      const fallbackStart =
        activeMode === mode ? data.numero_inicio : "";
      const fallbackCurrent =
        activeMode === mode ? data.numero_actual : "";
      const fallbackFinal =
        activeMode === mode ? data.numero_final : "";

      return {
        numberStart: normalizeLoadedSequenceValue(
          data[`${prefix}_number_start`] ?? fallbackStart,
          sequenceLength,
        ),
        numberCurrent: normalizeLoadedSequenceValue(
          data[`${prefix}_number_current`] ?? fallbackCurrent,
          sequenceLength,
        ),
        numberFinal: normalizeLoadedSequenceValue(
          data[`${prefix}_number_final`] ?? fallbackFinal,
          sequenceLength,
        ),
      };
    };

    // el isLoading se aplica para validar que la api aya sido consumido o cargado al 100 y recien ahi cargamos el componente
    const isLoading = ref(false);
    onMounted(async () => {
      const response = await axios
        .get("comprobantes/" + props.id)
        .catch((error) => errorActions(error));
      const data = await response.data;

      selectedVoucherTypeDescription.value =
        data.tipo_comprobante_descripcion || "";
      dataForm.value.voucherMode = normalizeVoucherMode(
        data.voucher_mode,
        data,
      );
      dataForm.value.serie = String(data.serie ?? "").trim().toUpperCase();
      dataForm.value.invoice_number = String(data.invoice_number ?? "")
        .trim()
        .toUpperCase();
      dataForm.value.vencimiento = data.vencimiento;
      dataForm.value.working = data.trabajando === 1 ? true : false;
      dataForm.value.fixed = data.fijado === 1 ? true : false;
      dataForm.value.state = data.estado === 1 ? true : false;
      dataForm.value.numberOfSendingSunat = data.numero_de_envio_sunat;

      const resolvedPhysicalSequence = resolveLoadedSequenceSet(
        data,
        "physical",
        dataForm.value.voucherMode,
      );

      const resolvedElectronicSequence = resolveLoadedSequenceSet(
        data,
        "electronic",
        dataForm.value.voucherMode,
      );

      modeDrafts.value.physical = {
        serie: data.serie ?? "",
        numberStart: resolvedPhysicalSequence.numberStart ?? "",
        numberCurrent: resolvedPhysicalSequence.numberCurrent ?? "",
        numberFinal: resolvedPhysicalSequence.numberFinal ?? "",
      };
      modeDrafts.value.electronic = {
        invoice_number: data.invoice_number ?? "",
        numberStart: resolvedElectronicSequence.numberStart ?? "",
        numberCurrent: resolvedElectronicSequence.numberCurrent ?? "",
        numberFinal: resolvedElectronicSequence.numberFinal ?? "",
      };
      applyModeDraft(dataForm.value.voucherMode);
      originalConfig.value = {
        numberCurrent: dataForm.value.voucherMode === "electronic"
          ? (resolvedElectronicSequence.numberCurrent ?? "")
          : (resolvedPhysicalSequence.numberCurrent ?? ""),
        numberFinal: dataForm.value.voucherMode === "electronic"
          ? (resolvedElectronicSequence.numberFinal ?? "")
          : (resolvedPhysicalSequence.numberFinal ?? ""),
        vencimiento: data.vencimiento ?? "",
      };

      if (!voucherSupportsExpiration(selectedVoucherTypeDescription.value)) {
        dataForm.value.vencimiento = "";
        originalConfig.value.vencimiento = "";
      }

      isLoading.value = true;
    });

    const showExpirationDate = computed(() =>
      voucherSupportsExpiration(selectedVoucherTypeDescription.value)
    );

    const isElectronicMode = computed(() =>
      dataForm.value.voucherMode === "electronic"
    );

    const sequenceLength = computed(() =>
      isElectronicMode.value
        ? ELECTRONIC_SEQUENCE_LENGTH
        : PHYSICAL_SEQUENCE_LENGTH
    );

    const sequencePlaceholder = computed(() =>
      isElectronicMode.value ? "0000000001" : "00000001"
    );

    const sequenceFinalPlaceholder = computed(() =>
      isElectronicMode.value ? "9999999999" : "99999999"
    );

    const sequenceModeHelpText = computed(() =>
      isElectronicMode.value
        ? "Electrónico activo: requiere serie E31/E32/E34 y secuencia exacta de 10 dígitos."
        : "Físico activo: requiere serie B01/B02/B14 y secuencia exacta de 8 dígitos."
    );

    const activeModeLabel = computed(() =>
      isElectronicMode.value ? "Electrónico" : "Físico"
    );

    const sequenceValidationSummary = computed(() =>
      isElectronicMode.value
        ? "Para comprobante electrónico se valida una serie E + 2 dígitos y números de inicio, actual y final de 10 dígitos exactos."
        : "Para comprobante físico se valida una serie fiscal como B01/B02/B14 y números de inicio, actual y final de 8 dígitos exactos."
    );

    const syncCurrentModeDraft = () => {
      if (dataForm.value.voucherMode === "electronic") {
        modeDrafts.value.electronic = {
          invoice_number: dataForm.value.invoice_number,
          numberStart: dataForm.value.numberStart,
          numberCurrent: dataForm.value.numberCurrent,
          numberFinal: dataForm.value.numberFinal,
        };

        return;
      }

      modeDrafts.value.physical = {
        serie: dataForm.value.serie,
        numberStart: dataForm.value.numberStart,
        numberCurrent: dataForm.value.numberCurrent,
        numberFinal: dataForm.value.numberFinal,
      };
    };

    const applyModeDraft = (mode) => {
      if (mode === "electronic") {
        dataForm.value.invoice_number = modeDrafts.value.electronic.invoice_number;
        dataForm.value.numberStart = modeDrafts.value.electronic.numberStart;
        dataForm.value.numberCurrent = modeDrafts.value.electronic.numberCurrent;
        dataForm.value.numberFinal = modeDrafts.value.electronic.numberFinal;
        return;
      }

      dataForm.value.serie = modeDrafts.value.physical.serie;
      dataForm.value.numberStart = modeDrafts.value.physical.numberStart;
      dataForm.value.numberCurrent = modeDrafts.value.physical.numberCurrent;
      dataForm.value.numberFinal = modeDrafts.value.physical.numberFinal;
    };

    const setVoucherMode = (mode) => {
      if (dataForm.value.voucherMode === mode) {
        return;
      }

      syncCurrentModeDraft();
      dataForm.value.voucherMode = mode;
      applyModeDraft(mode);
    };

    const sanitizeNumericField = (fieldName, maxLength) => {
      dataForm.value[fieldName] = String(dataForm.value[fieldName] ?? '')
        .replace(/\D/g, '')
        .slice(0, maxLength);
    };

    const sanitizePhysicalSerieField = () => {
      dataForm.value.serie = String(dataForm.value.serie ?? "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 4);
    };

    const sanitizeElectronicSerieField = () => {
      dataForm.value.invoice_number = String(dataForm.value.invoice_number ?? "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, ELECTRONIC_SERIE_MAX_LENGTH);
    };

    const isValidSequenceLength = (value, exactLength) =>
      new RegExp(`^\\d{${exactLength}}$`).test(
        String(value ?? '').trim()
      );

    const isValidPhysicalSerie = (value) =>
      /^[A-Z]\d{2,3}$/.test(
        String(value ?? "").trim().toUpperCase()
      );

    const isValidElectronicSerie = (value) =>
      /^E\d{2}$/.test(
        String(value ?? "").trim().toUpperCase()
      );

    const parseNumericValue = (value) => {
      const parsed = parseInt(value, 10);

      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const confirmSensitiveUpdate = async () => {
      const warnings = [];
      const previousCurrent = parseNumericValue(originalConfig.value.numberCurrent);
      const nextCurrent = parseNumericValue(dataForm.value.numberCurrent);
      const previousFinal = parseNumericValue(originalConfig.value.numberFinal);
      const nextFinal = parseNumericValue(dataForm.value.numberFinal);
      const previousExpiration = originalConfig.value.vencimiento || "";
      const nextExpiration = dataForm.value.vencimiento || "";
      const expirationChanged = showExpirationDate.value && previousExpiration !== nextExpiration;
      const finalChanged = previousFinal !== nextFinal;
      const currentChanged = previousCurrent !== nextCurrent;

      if (expirationChanged && !finalChanged) {
        warnings.push(
          `Se actualizará la fecha del lote actual de <strong>${previousExpiration || "sin fecha"}</strong> a <strong>${nextExpiration || "sin fecha"}</strong>. Los próximos comprobantes pendientes de ese tramo usarán esa nueva fecha, pero los documentos ya emitidos no cambian.`
        );
      }

      if (finalChanged && nextFinal > previousFinal) {
        warnings.push(
          `Se ampliará la secuencia desde <strong>${previousFinal + 1}</strong> hasta <strong>${nextFinal}</strong>. Los comprobantes pendientes hasta el <strong>${previousFinal}</strong> conservarán su vencimiento anterior y el nuevo tramo tomará la fecha <strong>${nextExpiration || previousExpiration || "sin fecha"}</strong>.`
        );
      }

      if (finalChanged && nextFinal < previousFinal) {
        warnings.push(
          `Estás reduciendo el número final de <strong>${previousFinal}</strong> a <strong>${nextFinal}</strong>. Esto puede dejar comprobantes fuera del rango operativo si no coincide con la secuencia autorizada por DGII.`
        );
      }

      if (currentChanged) {
        warnings.push(
          `Estás modificando el número actual de <strong>${previousCurrent}</strong> a <strong>${nextCurrent}</strong>. Si no coincide con el último comprobante realmente emitido, puedes provocar rechazos o inconsistencias fiscales.`
        );
      }

      if (warnings.length <= 0) {
        return true;
      }

      const result = await Swal.fire({
        icon: "warning",
        title: "Cambio sensible en la secuencia",
        html: `${warnings.join("<br><br>")}<br><br><strong>Confirma que deseas continuar con esta actualización.</strong>`,
        showCancelButton: true,
        confirmButtonColor: "#315df5",
        cancelButtonColor: "#dc2626",
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar",
        width: "560px",
      });

      observer.observe(
        document.querySelector(".swal2-container"),
        { box: "content-box" },
        10
      );

      return result.isConfirmed;
    };

    const update = async () => {
      if (isElectronicMode.value) {
        sanitizeElectronicSerieField();
        if (!isValidElectronicSerie(dataForm.value.invoice_number)) {
          errorActions(
            "Ingresa una serie electrónica válida con formato <strong>E + 2 dígitos</strong>. Ejemplos: <strong>E31</strong>, <strong>E32</strong> o <strong>E34</strong>."
          );
          observer.observe(
            document.querySelector(".swal2-container"),
            { box: "content-box" },
            10
          );
          return;
        }
      } else {
        sanitizePhysicalSerieField();
        if (!isValidPhysicalSerie(dataForm.value.serie)) {
          errorActions(
            "Ingresa una serie física válida con formato <strong>letra + 2 o 3 dígitos</strong>. Ejemplos: <strong>B01</strong>, <strong>B02</strong> o <strong>B14</strong>."
          );
          observer.observe(
            document.querySelector(".swal2-container"),
            { box: "content-box" },
            10
          );
          return;
        }
      }
      if (!isValidSequenceLength(dataForm.value.numberStart, sequenceLength.value)) {
        errorActions(
          `Ingresa un <strong>número de inicio</strong> válido de <strong>${sequenceLength.value} dígitos exactos</strong> para comprobante <strong>${isElectronicMode.value ? "electrónico" : "físico"}</strong>.`
        );
        observer.observe(
          document.querySelector(".swal2-container"),
          { box: "content-box" },
          10
        );
        return;
      }
      if (!isValidSequenceLength(dataForm.value.numberCurrent, sequenceLength.value)) {
        errorActions(
          `Ingresa un <strong>número actual</strong> válido de <strong>${sequenceLength.value} dígitos exactos</strong> para comprobante <strong>${isElectronicMode.value ? "electrónico" : "físico"}</strong>.`
        );
        observer.observe(
          document.querySelector(".swal2-container"),
          { box: "content-box" },
          10
        );
        return;
      }
      if (!isValidSequenceLength(dataForm.value.numberFinal, sequenceLength.value)) {
        errorActions(
          `Ingresa un <strong>número final</strong> válido de <strong>${sequenceLength.value} dígitos exactos</strong> para comprobante <strong>${isElectronicMode.value ? "electrónico" : "físico"}</strong>.`
        );
        observer.observe(
          document.querySelector(".swal2-container"),
          { box: "content-box" },
          10
        );
        return;
      }

      const confirmed = await confirmSensitiveUpdate();
      if (!confirmed) {
        return;
      }

      syncCurrentModeDraft();
      spinner.value = true;

      const payload = {
        voucherMode: dataForm.value.voucherMode,
        serie: String(modeDrafts.value.physical.serie ?? "").trim().toUpperCase(),
        numberStart: dataForm.value.numberStart,
        invoice_number: String(modeDrafts.value.electronic.invoice_number ?? "").trim().toUpperCase() || null,
        numberFinal: dataForm.value.numberFinal,
        numberCurrent: dataForm.value.numberCurrent,
        physical_number_start: modeDrafts.value.physical.numberStart,
        physical_number_current: modeDrafts.value.physical.numberCurrent,
        physical_number_final: modeDrafts.value.physical.numberFinal,
        electronic_number_start: modeDrafts.value.electronic.numberStart,
        electronic_number_current: modeDrafts.value.electronic.numberCurrent,
        electronic_number_final: modeDrafts.value.electronic.numberFinal,
        working: dataForm.value.working ? 1 : 0,
        fixed: dataForm.value.fixed ? 1 : 0,
        state: dataForm.value.state ? 1 : 0,
        numberOfSendingSunat: dataForm.value.numberOfSendingSunat,
      };

      if (showExpirationDate.value && dataForm.value.vencimiento) {
        payload.vencimiento = dataForm.value.vencimiento;
      }

      const response = await axios
        .put(
          "comprobantes/" + props.id,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .catch(function (error) {
          errorActions(
            error?.response?.data?.detail ||
              error?.response?.data?.message ||
              error?.message ||
              error
          );
          return null;
        });
      if (!response) {
        spinner.value = false;
        return;
      }
      const data = await response.data;
      if (data.msg === true) {
        await props.activeModal(false);
        await props.getdata();

        if (data.warning) {
          warningActions(data.warning);
        } else {
          confirmationOfUpdate(dataForm.value.serie);
        }
      } else if (data.msg === "Request failed with status code 500") {
        errorActions("Request failed with status code 500");
      } else if (data.status === 404) {
        errorActions(
          `Los datos del comprobante con la serie <trong>${dataForm.value.serie} </strong> que estas intentando actualizar no existe en nuestra base de datos`
        );
      } else if (data.msg === "existSerie") {
        errorActions(
          `Ya existe un comprobante usando la serie <strong>${dataForm.value.serie}</strong>`
        );
        spinner.value = false; //dejando activo al boton
      } else {
        errorActions(
          `Lo sentimos no pudimos actualizar el comprobante con la serie  <trong>${dataForm.value.serie} </strong>`
        );
      }

      observer.observe(
        document.querySelector(".swal2-container"),
        { box: "content-box" },
        10
      );
    };

    return {
      isActive,
      dataForm,
      update,
      isElectronicMode,
      isLoading,
      spinner,
      showExpirationDate,
      setVoucherMode,
      sequenceLength,
      sequencePlaceholder,
      sequenceFinalPlaceholder,
      sequenceModeHelpText,
      sequenceValidationSummary,
      activeModeLabel,
      sanitizeNumericField,
      sanitizePhysicalSerieField,
      sanitizeElectronicSerieField,
      ELECTRONIC_SERIE_MAX_LENGTH,
      config,
    };
  },
};
</script>
